interface OllamaResponse {
  model: string
  created_at: string
  message: {
    role: string
    content: string
  }
  done: boolean
}

interface ChatMessage {
  role: 'system' | 'user' | 'assistant'
  content: string
}

interface CacheEntry {
  response: string
  timestamp: number
  contextHash: string
}

interface OptimizedPrompt {
  key: string
  system: string
  template: string
  maxTokens: number
}

export class OllamaService {
  private baseUrl: string
  private model: string
  private responseCache: Map<string, CacheEntry> = new Map()
  private readonly cacheExpiry: number = 60 * 60 * 1000 // 1 hora
  private readonly maxContextLength: number = 1024 // Reducido para eficiencia
  private isModelWarmedUp: boolean = false
  private warmupPromise: Promise<void> | null = null
  private enabled: boolean = false
  
  // Prompts pre-optimizados para máxima eficiencia
  private optimizedPrompts: Map<string, OptimizedPrompt> = new Map([
    ['crud_basic', {
      key: 'crud_basic',
      system: 'Experto CRUD. Código limpio y funcional.',
      template: 'CRUD {entity} campos: {fields}. React+TS+Tailwind.',
      maxTokens: 800
    }],
    ['api_endpoint', {
      key: 'api_endpoint',
      system: 'Experto API REST. Express+TypeScript.',
      template: 'Endpoint {operation} para {entity}. Validación+Error handling.',
      maxTokens: 600
    }],
    ['react_component', {
      key: 'react_component',
      system: 'Experto React. Componentes modernos.',
      template: 'Componente {name}: {description}. TS+Hooks+Tailwind.',
      maxTokens: 700
    }],
    ['analyze_crud', {
      key: 'analyze_crud',
      system: 'Analista requerimientos. Solo JSON.',
      template: 'Extraer JSON CRUD de: {input}',
      maxTokens: 300
    }]
  ])

  constructor() {
    this.baseUrl = 'http://localhost:11434'
    this.model = 'hf.co/Qwen/Qwen2.5-Coder-1.5B-Instruct-GGUF:Q8_0'

    // Feature flag para habilitar Ollama explícitamente
    let envFlag = false
    try {
      // Vite: VITE_OLLAMA_ENABLED="true" para habilitar
      envFlag = typeof import.meta !== 'undefined' && (import.meta as any)?.env?.VITE_OLLAMA_ENABLED === 'true'
    } catch {}
    let lsFlag = false
    try {
      // Override por localStorage: OLLAMA_ENABLED = '1'
      lsFlag = typeof window !== 'undefined' && window?.localStorage?.getItem('OLLAMA_ENABLED') === '1'
    } catch {}
    this.enabled = !!(envFlag || lsFlag)

    // Solo intentar warmup si está habilitado
    if (this.enabled) {
      this.warmupModel() // Precalentar modelo en construcción
    }
  }

  // Precalentar el modelo para respuestas más rápidas
  private async warmupModel(): Promise<void> {
    if (!this.enabled) return
    if (this.warmupPromise) return this.warmupPromise
    
    this.warmupPromise = (async () => {
      try {
        const isAvailable = await this.isAvailable()
        if (!isAvailable) return

        // Consulta pequeña para cargar el modelo en memoria
        await fetch(`${this.baseUrl}/api/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            model: this.model,
            messages: [{ role: 'user', content: 'Hello' }],
            stream: false,
            options: {
              num_ctx: this.maxContextLength,
              temperature: 0.1,
              max_tokens: 10,
              num_thread: 8 // Usar todos los cores del i5
            }
          })
        })
        
        this.isModelWarmedUp = true
      } catch (error) {
      }
    })()
    
    return this.warmupPromise
  }

  // Generar hash para caching
  private generateHash(input: string): string {
    let hash = 0
    for (let i = 0; i < input.length; i++) {
      const char = input.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36)
  }

  // Limpiar cache expirado
  private cleanExpiredCache(): void {
    const now = Date.now()
    for (const [key, entry] of this.responseCache.entries()) {
      if (now - entry.timestamp > this.cacheExpiry) {
        this.responseCache.delete(key)
      }
    }
  }

  // Optimizar contexto para mínima latencia
  private optimizeContext(messages: ChatMessage[], systemPrompt?: string): ChatMessage[] {
    // Mantener solo el contexto más reciente y relevante
    const maxMessages = 3 // Limitar historial para CPU sin GPU
    const recentMessages = messages.slice(-maxMessages)
    
    // Truncar mensajes muy largos
    const truncatedMessages = recentMessages.map(msg => ({
      ...msg,
      content: msg.content.length > 500 ? msg.content.substring(0, 500) + '...' : msg.content
    }))

    return [
      ...(systemPrompt ? [{ role: 'system' as const, content: systemPrompt }] : []),
      ...truncatedMessages
    ]
  }

  async generateResponse(
    messages: ChatMessage[], 
    systemPrompt?: string
  ): Promise<string> {
    try {
      if (!this.enabled) {
        throw new Error('Servicio de IA deshabilitado. Habilítalo con VITE_OLLAMA_ENABLED="true" o localStorage OLLAMA_ENABLED=1')
      }
      // Asegurar que el modelo esté caliente
      await this.warmupModel()
      
      // Limpiar cache expirado
      this.cleanExpiredCache()
      
      // Generar hash para caching
      const contextHash = this.generateHash(
        JSON.stringify({ messages: messages.slice(-2), system: systemPrompt })
      )
      
      // Verificar cache
      const cached = this.responseCache.get(contextHash)
      if (cached && Date.now() - cached.timestamp < this.cacheExpiry) {
        return cached.response
      }

      // Optimizar contexto para CPU sin GPU
      const optimizedMessages = this.optimizeContext(messages, systemPrompt)

      const response = await fetch(`${this.baseUrl}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: AbortSignal.timeout(30000), // Timeout para evitar bloqueos
        body: JSON.stringify({
          model: this.model,
          messages: optimizedMessages,
          stream: false,
          options: {
            // Configuraciones optimizadas para CPU i5 sin GPU
            num_ctx: this.maxContextLength, // Contexto reducido
            temperature: 0.3, // Menos aleatorio = más rápido
            top_p: 0.8,
            max_tokens: 800,
            num_thread: 8, // Usar todos los threads del i5 12gen
            repeat_penalty: 1.1,
            num_predict: 800
          }
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data: OllamaResponse = await response.json()
      const result = data.message.content
      
      // Guardar en cache
      this.responseCache.set(contextHash, {
        response: result,
        timestamp: Date.now(),
        contextHash
      })
      
      return result

    } catch (error) {
      throw new Error('Error al comunicarse con el asistente IA. Verifica que Ollama esté ejecutándose.')
    }
  }

  // Método optimizado para generar CRUD con prompts ultra-eficientes
  async generateCrudCode(entityName: string, fields: any[]): Promise<string> {
    const cacheKey = `crud_${entityName}_${JSON.stringify(fields).substring(0, 50)}`
    
    // Verificar cache específico para CRUDs
    const cached = this.responseCache.get(cacheKey)
    if (cached && Date.now() - cached.timestamp < this.cacheExpiry) {
      return cached.response
    }

    // Prompt super optimizado para CPU sin GPU
    const prompt = this.optimizedPrompts.get('crud_basic')!
    const fieldsList = fields.map(f => `${f.name}:${f.type}${f.required ? '*' : ''}`).join(',')
    
    const optimizedSystemPrompt = prompt.system
    const optimizedUserPrompt = prompt.template
      .replace('{entity}', entityName)
      .replace('{fields}', fieldsList)

    try {
      const response = await this.generateOptimizedResponse([
        { role: 'user', content: optimizedUserPrompt }
      ], optimizedSystemPrompt, prompt.maxTokens)
      
      // Cache específico
      this.responseCache.set(cacheKey, {
        response,
        timestamp: Date.now(),
        contextHash: cacheKey
      })
      
      return response
    } catch (error) {
      // Fallback con respuesta predefinida
      return this.getFallbackCrudResponse(entityName, fields)
    }
  }

  async generateApiEndpoint(entityName: string, operation: string): Promise<string> {
    const systemPrompt = `Eres un experto desarrollador backend especializado en APIs REST con Node.js y Express.
    Genera código completo y funcional para endpoints de API siguiendo las mejores prácticas.`

    const userPrompt = `Genera un endpoint de API REST para la operación "${operation}" de la entidad "${entityName}".
    
    Incluye:
    - Ruta del endpoint
    - Controlador con lógica completa
    - Validaciones de entrada
    - Manejo de errores
    - Respuestas apropiadas
    - Middleware de autenticación si es necesario
    
    Usa Express.js, TypeScript y sigue las mejores prácticas de APIs REST.`

    return this.generateResponse([
      { role: 'user', content: userPrompt }
    ], systemPrompt)
  }

  async generateReactComponent(componentName: string, description: string): Promise<string> {
    const systemPrompt = `Eres un experto desarrollador frontend especializado en React y TypeScript.
    Genera componentes React modernos, reutilizables y siguiendo las mejores prácticas.`

    const userPrompt = `Genera un componente React llamado "${componentName}" que ${description}.
    
    Requisitos:
    - TypeScript completo con interfaces
    - Hooks modernos (useState, useEffect, etc.)
    - Estilos con Tailwind CSS
    - Manejo de estados y errores
    - Componente funcional con props tipadas
    - Documentación JSDoc si es complejo
    
    El código debe ser limpio, modular y reutilizable.`

    return this.generateResponse([
      { role: 'user', content: userPrompt }
    ], systemPrompt)
  }

  async analyzeCrudRequirements(userInput: string): Promise<{
    entityName: string
    fields: Array<{
      name: string
      type: string
      required: boolean
      validation?: string
    }>
    operations: string[]
  }> {
    const systemPrompt = `Eres un analista de sistemas experto en extraer requerimientos de CRUD de descripciones en lenguaje natural.
    Tu trabajo es analizar la entrada del usuario y extraer una estructura JSON clara con la entidad, campos y operaciones requeridas.
    
    Responde SOLO con un JSON válido, sin explicaciones adicionales.`

    const userPrompt = `Analiza esta descripción y extrae los requerimientos para un CRUD:
    
    "${userInput}"
    
    Extrae y devuelve un JSON con esta estructura exacta:
    {
      "entityName": "nombre de la entidad",
      "fields": [
        {
          "name": "nombre del campo",
          "type": "string|number|boolean|date|text|email|select",
          "required": true/false,
          "validation": "descripción opcional de validación"
        }
      ],
      "operations": ["create", "read", "update", "delete"]
    }`

    const response = await this.generateResponse([
      { role: 'user', content: userPrompt }
    ], systemPrompt)

    try {
      // Intentar parsear la respuesta como JSON
      const cleanResponse = response.trim()
      const jsonMatch = cleanResponse.match(/\{[\s\S]*\}/)
      
      if (jsonMatch) {
        return JSON.parse(jsonMatch[0])
      } else {
        // Si no encuentra JSON válido, usar fallback
        throw new Error('No se pudo extraer JSON válido')
      }
    } catch (error) {
      // Fallback con valores por defecto
      return {
        entityName: 'Entidad',
        fields: [
          { name: 'id', type: 'number', required: true },
          { name: 'name', type: 'string', required: true }
        ],
        operations: ['create', 'read', 'update', 'delete']
      }
    }
  }

  // Método ultra-optimizado para consultas específicas
  private async generateOptimizedResponse(
    messages: ChatMessage[],
    systemPrompt: string,
    maxTokens: number
  ): Promise<string> {
    if (!this.enabled) {
      throw new Error('Servicio de IA deshabilitado. Habilítalo con VITE_OLLAMA_ENABLED="true" o localStorage OLLAMA_ENABLED=1')
    }
    await this.warmupModel()
    
    const response = await fetch(`${this.baseUrl}/api/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal: AbortSignal.timeout(25000), // Timeout más corto
      body: JSON.stringify({
        model: this.model,
        messages: [
          { role: 'system', content: systemPrompt },
          ...messages
        ],
        stream: false,
        options: {
          num_ctx: 512, // Contexto mínimo para máxima velocidad
          temperature: 0.1, // Muy determinista = más rápido
          top_p: 0.7,
          max_tokens: maxTokens,
          num_thread: 8, // Usar todos los threads
          repeat_penalty: 1.0,
          num_predict: maxTokens,
          stop: ['```', '---', '###'] // Paradas tempranas
        }
      })
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data: OllamaResponse = await response.json()
    return data.message.content
  }

  // Respuestas fallback predefinidas para máxima velocidad
  private getFallbackCrudResponse(entityName: string, fields: any[]): string {
    const fieldsList = fields.map(f => `  ${f.name}: ${f.type}${f.required ? ' (required)' : ''}`).join('\n')
    
    return `🚀 CRUD generado para ${entityName}

**Interfaz TypeScript:**
\`\`\`typescript
interface ${entityName} {
  id: number
${fieldsList}
  createdAt: Date
  updatedAt: Date
}
\`\`\`

**Servicio:**
\`\`\`typescript
class ${entityName}Service {
  private baseUrl = '/api/${entityName.toLowerCase()}s'
  
  async getAll(): Promise<${entityName}[]> {
    const response = await fetch(this.baseUrl)
    return response.json()
  }
  
  async create(data: Omit<${entityName}, 'id' | 'createdAt' | 'updatedAt'>): Promise<${entityName}> {
    const response = await fetch(this.baseUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    })
    return response.json()
  }
}
\`\`\`

**Componente React:**
\`\`\`tsx
const ${entityName}List: React.FC = () => {
  const [items, setItems] = useState<${entityName}[]>([])
  const [loading, setLoading] = useState(true)
  
  useEffect(() => {
    loadItems()
  }, [])
  
  const loadItems = async () => {
    try {
      setLoading(true)
      const service = new ${entityName}Service()
      const data = await service.getAll()
      setItems(data)
    } catch (error) {
    } finally {
      setLoading(false)
    }
  }
  
  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">${entityName}s</h1>
      {loading ? (
        <div>Loading...</div>
      ) : (
        <div className="grid gap-4">
          {items.map(item => (
            <div key={item.id} className="border p-4 rounded">
              {/* Render item fields */}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
\`\`\`

✅ CRUD completo generado con fallback rápido!`
  }

  // Optimizar API endpoints con cache
  async generateApiEndpointOptimized(entityName: string, operation: string): Promise<string> {
    const cacheKey = `api_${entityName}_${operation}`
    const cached = this.responseCache.get(cacheKey)
    
    if (cached && Date.now() - cached.timestamp < this.cacheExpiry) {
      return cached.response
    }

    const prompt = this.optimizedPrompts.get('api_endpoint')!
    const optimizedUserPrompt = prompt.template
      .replace('{operation}', operation)
      .replace('{entity}', entityName)

    try {
      const response = await this.generateOptimizedResponse([
        { role: 'user', content: optimizedUserPrompt }
      ], prompt.system, prompt.maxTokens)
      
      this.responseCache.set(cacheKey, {
        response,
        timestamp: Date.now(),
        contextHash: cacheKey
      })
      
      return response
    } catch (error) {
      return `🚀 Endpoint ${operation} para ${entityName} generado con fallback`
    }
  }

  // Análisis rápido con cache agresivo
  async quickAnalyze(userInput: string): Promise<any> {
    const inputHash = this.generateHash(userInput)
    const cacheKey = `analyze_${inputHash}`
    
    const cached = this.responseCache.get(cacheKey)
    if (cached && Date.now() - cached.timestamp < this.cacheExpiry) {
      return JSON.parse(cached.response)
    }

    const prompt = this.optimizedPrompts.get('analyze_crud')!
    const optimizedPrompt = prompt.template.replace('{input}', userInput.substring(0, 200))

    try {
      const response = await this.generateOptimizedResponse([
        { role: 'user', content: optimizedPrompt }
      ], prompt.system, prompt.maxTokens)
      
      this.responseCache.set(cacheKey, {
        response,
        timestamp: Date.now(),
        contextHash: cacheKey
      })
      
      return JSON.parse(response)
    } catch (error) {
      // Fallback inteligente basado en palabras clave
      return this.getSmartFallbackAnalysis(userInput)
    }
  }

  // Fallback inteligente para análisis
  private getSmartFallbackAnalysis(input: string): any {
    const lowInput = input.toLowerCase()
    let entityName = 'Entity'
    const fields = [{ name: 'id', type: 'number', required: true }]
    
    // Detectar entidad
    if (lowInput.includes('producto')) entityName = 'Producto'
    else if (lowInput.includes('usuario')) entityName = 'Usuario'
    else if (lowInput.includes('cliente')) entityName = 'Cliente'
    else if (lowInput.includes('pedido')) entityName = 'Pedido'
    
    // Detectar campos comunes
    if (lowInput.includes('nombre')) fields.push({ name: 'nombre', type: 'string', required: true })
    if (lowInput.includes('email')) fields.push({ name: 'email', type: 'email', required: true })
    if (lowInput.includes('precio')) fields.push({ name: 'precio', type: 'number', required: true })
    if (lowInput.includes('descripcion')) fields.push({ name: 'descripcion', type: 'text', required: false })
    
    return {
      entityName,
      fields,
      operations: ['create', 'read', 'update', 'delete']
    }
  }

  // Limpiar cache manualmente si es necesario
  clearCache(): void {
    this.responseCache.clear()
  }

  // Estadísticas de rendimiento
  getPerformanceStats(): {
    cacheSize: number
    cacheHitRate: number
    modelWarmed: boolean
  } {
    return {
      cacheSize: this.responseCache.size,
      cacheHitRate: 0, // Se podría implementar un contador
      modelWarmed: this.isModelWarmedUp
    }
  }

  async isAvailable(): Promise<boolean> {
    if (!this.enabled) return false
    try {
      const response = await fetch(`${this.baseUrl}/api/version`, {
        method: 'GET',
        signal: AbortSignal.timeout(3000) // Timeout más corto
      })
      return response.ok
    } catch (error) {
      return false
    }
  }
}

export const ollamaService = new OllamaService()
